import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'flowbite-react';
import { isSuccessResponse } from '../../api/apiClient';
import {
  listWorkflowExecutions,
  getExecutionDetail,
  type WorkflowExecutionSummary,
  type ExecutionDetail,
} from '../../api/workflowInspector';
import ExecutionList, { PAGE_SIZE } from './ExecutionList';
import ExecutionSummary from './ExecutionSummary';
import StepTimeline from './StepTimeline';
import CompliancePanel from './CompliancePanel';

const WorkflowInspectorPage: React.FC = () => {
  // List state
  const [executions, setExecutions] = useState<WorkflowExecutionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [listLoading, setListLoading] = useState(true);

  // Detail state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ExecutionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Fetch execution list
  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await listWorkflowExecutions({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: statusFilter ? (statusFilter as any) : undefined,
      });

      if (isSuccessResponse(response)) {
        setExecutions(response.data.Records);
        setTotal(response.meta.Total_Records);
        setHasMore((response.data as any).Has_More ?? false);
      } else {
        console.error('Failed to fetch executions:', response.message);
        setExecutions([]);
      }
    } catch (err) {
      console.error('Failed to fetch executions:', err);
    } finally {
      setListLoading(false);
    }
  }, [page, statusFilter]);

  // Fetch execution detail
  const fetchDetail = useCallback(async (executionId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await getExecutionDetail(executionId);

      if (isSuccessResponse(response)) {
        const record = response.data.Records[0];
        if (record) {
          setDetail(record);
        } else {
          setDetailError('Execution not found');
          setDetail(null);
        }
      } else {
        setDetailError(response.message || 'Failed to load execution detail');
        setDetail(null);
      }
    } catch (err: any) {
      setDetailError(err.message || 'Failed to load execution detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (selectedId) {
      fetchDetail(selectedId);
    }
  }, [selectedId, fetchDetail]);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(0);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Workflow Executions</h1>
        <p className="text-sm text-gray-500 mt-1">
          Inspect completed workflow executions, decision frames, and standards compliance.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6" style={{ minHeight: '70vh' }}>
        {/* Left panel: execution list */}
        <div className="w-full md:w-1/3 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <ExecutionList
            executions={executions}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={listLoading}
            total={total}
            hasMore={hasMore}
            page={page}
            onPageChange={setPage}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />
        </div>

        {/* Right panel: detail */}
        <div className="w-full md:w-2/3 space-y-4">
          {!selectedId ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-sm">Select an execution to view details</p>
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : detailError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{detailError}</p>
            </div>
          ) : detail ? (
            <>
              <ExecutionSummary execution={detail.Execution} />
              <StepTimeline
                steps={detail.Steps}
                decisionFrames={detail.Decision_Frames}
              />
              <CompliancePanel
                standards={detail.Compliance.Standards}
                summary={detail.Compliance.Summary}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WorkflowInspectorPage;
